import React, { useState, useMemo, useContext } from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import DataTable from "react-data-table-component";
import { NotificationManager } from "react-notifications";
import { makeStyles } from "@mui/styles";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import { deleteMarketFile, downloadFile } from "services/ConfigServices";
import { marketcontractStatusDataTab } from "variables/lists/markets";
import useOtherfilesTable from "./useOtherfilesTable.js";
import EditOtherfilesDialog from "./EditOtherfilesDialog";
import EditOtherfilesStatusDialog from "./EditOtherfilesStatusDialog";
import CustomDeleteButton from "components/CustomDeleteButton/CustomDeleteButton.js";
import UserService from "services/UserService";
import Button from "components/CustomButtons/Button.js";
import styled from "styled-components";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import { marketcontracts } from "variables/markets";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import { withStyles } from "@mui/styles";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import CustomDialogTitle from "components/Dialog/CustomDialogTitle.js";
import MarketsContext from "context/MarketsContext";


const GroupCell = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Container = styled.div`
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  border-top: 4px solid rgba(255, 255, 255, 1);
  background-color: white;
  margin-top: -0.5px;
`;

const ExpandableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 4rem 2rem;
`;
const ExpandableElement = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${(props) => props.marginleft};
`;

const ElementTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementData = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementDataDownload = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
  cursor: pointer;
  text-decoration: underline;
`;

const NoData = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  opacity: 1;
`;


const DialogContent = withStyles((theme) => ({
  root: {
    padding: '16px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: '8px',
  },
}))(MuiDialogActions);

const ExpandableComponent = props => {
  const { data } = props;
  return (
    <Container>
      <ExpandableContainer>
        <ExpandableElement>
          <ElementTitle>{marketcontracts.legalentity} : </ElementTitle>
          <ElementData>{data.parameters?.legalentity}</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{marketcontracts.representedby} : </ElementTitle>
          <ElementData>{data.parameters?.representedby}</ElementData>
        </ExpandableElement>
        { data.parameters?.associatedfiles && 
          <ExpandableElement>
          <ElementTitle>{marketcontracts.associatedfiles} : </ElementTitle>
          <ElementDataDownload onClick={downloadGED(data.parameters?.associatedfiles.fileId, data.parameters?.associatedfiles.fileName)}>{data.parameters?.associatedfiles.fileName}</ElementDataDownload>
        </ExpandableElement>
        }
      </ExpandableContainer>
    </Container>
  );
};

const downloadGED = (fileId, fileName) => async () => {
  axios
    .get(
      downloadFile(fileId),
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      }
    )
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); //or any other extension
      document.body.appendChild(link);
      link.click();
    });
};


const useStyles = makeStyles(styles);

const RTOtherfiles = () => {
  const permis = JSON.parse(localStorage.getItem("permission")) || [];
  const marketContext = useContext(MarketsContext);
  const { market, setMarket } = marketContext;
  const markets_admin = permis.includes("markets_admin") && market.marketAdminVisibility;;
  const { gridState, refetch, setSelectedOtherFiles } = useOtherfilesTable();
  
  const advancedSearchContext = useContext(AdvancedSearchContext);
  const { groupInfoListById, setPage } = advancedSearchContext;
  const [open, setOpen] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);

  let dataFiles = gridState.data;

  const classes = useStyles();

  const columns = [
    {
      name: marketcontracts.doctype,
      selector: (row) => row.parameters.doctype,
      sortable: true,
      maxWidth: "13rem",
    }, 
    {
      name: marketcontracts.docname,
      selector: (row) => row.parameters.docname,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: marketcontracts.docnumber,
      selector: (row) => row.parameters.docnumber,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: marketcontracts.status,
      sortable: true,
      maxWidth: "12rem",
      cell: (row) => row.parameters.status ?
        <div style={{ color: marketcontractStatusDataTab.find((x) => x.id == row.parameters.status).color, fontWeight: "bold" }}>
          {marketcontractStatusDataTab.find((x) => x.id == row.parameters.status).name}
        </div> : <></>
    }, 
    {
      name: marketcontracts.datebegin,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.datebegin && moment(row.parameters.datebegin).isValid()
          ? moment(row.parameters.datebegin).format("DD/MM/YYYY")
          : "",
    },
    {
      name: marketcontracts.dateend,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.dateend && moment(row.parameters.dateend).isValid()
          ? moment(row.parameters.dateend).format("DD/MM/YYYY")
          : "",
    },
    {
      name: marketcontracts.datesignature,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.datesignature && moment(row.parameters.datesignature).isValid()
          ? moment(row.parameters.datesignature).format("DD/MM/YYYY")
          : "",
    },
    {
      name: "",
      sortable: false,
      width: "10rem",
      // eslint-disable-next-line react/button-has-type
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <>
          <div className="actions-right">
            {
              markets_admin && 
                <Tooltip title="Changer le statut">
                  <Button
                    onClick={editFileStatus(row)}
                    color={"info"}
                    className={classes.actionButton}
                  >
                    <ExitToAppIcon fontSize="large" />
                  </Button>
                </Tooltip>
            }{markets_admin && (
              <>
            <Tooltip title="Editer la pièce">
              <Button
                onClick={editFile(row)}
                color={"warning"}
                className={classes.actionButton}
              >
                <EditIcon fontSize="large" />
              </Button>
            </Tooltip>
            
              <Tooltip title="Supprimer la pièce">
                <CustomDeleteButton
                  deleteTitle={"Suppression de la pièce"}
                  deleteSubTitle={`Êtes-vous sûr de vouloir supprimer la pièce?`}
                  handleSupprimer={deleteFileFunction(row)}
                />
              </Tooltip>
              </>
            )}
          </div>
        </>
      ),
    },
  ];

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  const handleClickStatusOpen = () => {
    setOpenStatus(true);
  };
  const handleEditStatusClose = () => {
    setOpenStatus(false);
  };

  const editFile = (row) => async () => {
    let obj = dataFiles.find((o) => o.id === row.id);
    if (obj) {
      setCurrentFile(obj);
      handleClickOpen();
    }
  }

  const editFileStatus = (row) => async () => {
    let obj = dataFiles.find((o) => o.id === row.id);
    if (obj) {
      setCurrentFile(obj);
      handleClickStatusOpen();
    }
  }

  const deleteFileFunction = (row) => async () => {
    let obj = dataFiles.find((o) => o.id === row.id);
    let idSelected = obj.id;
    let url = deleteMarketFile(idSelected);
    axios
      .delete(url, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      })
      .then((res) => {
        if (res.status == 200) {
          setNotification(
            "success",
            `La pièce '${obj.parameters.name} a été supprimé`
          );
          refetch();
        } else setNotification("error", "Une erreur est survenue");
      })
      .catch((error) => {
        setNotification("error", "Une erreur est survenue");
      });
  };

  const setNotification = (status, text) => {
    if (status === "success") {
      NotificationManager.success(text);
    } else {
      NotificationManager.error(text);
    }
  };

  const handleSelectedRowsChange = (selection) => {
    setSelectedOtherFiles(selection.selectedRows);
  };
  
  return (
    <>
      <DataTable
          paginationRowsPerPageOptions={[10,20,30,50,100]}
        noDataComponent={<NoData>Aucune pièce de marché à afficher</NoData>}
        noHeader={true}
        columns={columns}
        persistTableHead
        pagination
        subHeader
        progressPending={gridState.loading}
        progressComponent={<LinearProgress />}
        onSelectedRowsChange={handleSelectedRowsChange}
        selectableRows={true}
        expandOnRowClicked
        expandableRows
        expandableRowDisabled={(row) => row.disabled}
        expandableRowsComponent={ExpandableComponent}
        paginationComponentOptions={{
          noRowsPerPage: false,
          rowsPerPageText: "Lignes par page",
          rangeSeparatorText: "sur",
          selectAllRowsItem: false,
          selectAllRowsItemText: "Selectionner toutes les lignes",
        }}
        {...gridState}
      />
      <Dialog
        onClose={handleEditClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={open}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditClose}>
          Modification de la pièce de marché
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditOtherfilesDialog
            marketfile={currentFile}
            closeModal={handleEditClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
      <Dialog
        onClose={handleEditStatusClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={openStatus}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditStatusClose}>
          Modification du statut de la pièce
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditOtherfilesStatusDialog
            marketfile={currentFile}
            closeModal={handleEditStatusClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </>
  );
};

export default RTOtherfiles;
